import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MISSIONS_CATEGORY_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

import { FreeTicket } from '../FreeTicket';
import type { GetMoreTickets_taskInterface } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true })`
  grid-area: getMoreTickets;
`;

type Props = {
  dailyWheelTicketTask: Nullable<GetMoreTickets_taskInterface>;
  dailyTasksExpiresAt: Nullable<Date>;
  rarity: Rarity;
};

export const GetMoreTickets = ({
  dailyWheelTicketTask,
  dailyTasksExpiresAt,
  rarity,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const { isIosApp, postMessage } = useIsMobileApp();
  const navigate = useNavigate();
  const commonPlayMissionPath = generateSportContextPath(
    ANY_SPORT_MISSIONS_CATEGORY_RARITY,
    {
      params: {
        category: 'play',
        rarity: Rarity.common,
      },
    }
  );

  const showMissionPage = currentUser?.showMissionPage;

  return (
    <Root>
      {!!dailyWheelTicketTask && (
        <FreeTicket
          task={dailyWheelTicketTask}
          nextFreeTicketIn={dailyTasksExpiresAt ?? undefined}
        />
      )}
      {rarity === Rarity.common && !!showMissionPage && (
        <Vertical center>
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage
              id="marketplace.wheel.needMoreTickets"
              defaultMessage="Need more tickets?"
            />
          </LabelM>
          <Button
            color="secondary"
            size="compact"
            onClick={() => {
              if (isIosApp) {
                postMessage('openMissions', {
                  category: 'play',
                  rarity: Rarity.common,
                });
              } else {
                navigate(commonPlayMissionPath);
              }
            }}
          >
            <FormattedMessage
              id="marketplace.wheel.unlockSpins"
              defaultMessage="Unlock spins"
            />
          </Button>
        </Vertical>
      )}
    </Root>
  );
};

GetMoreTickets.fragments = {
  taskInterface: gql`
    fragment GetMoreTickets_taskInterface on TaskInterface {
      id
      ...FreeTicket_TaskInterface
    }
    ${FreeTicket.fragments.TaskInterface}
  ` as TypedDocumentNode<GetMoreTickets_taskInterface>,
};
